import React, {useState, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import UIKIT from 'uikit';
import {vbankRefundAction, vbankRefundResetAction} from '../../../actions/order/action';
import {BANK_CODES} from '../../../ConfigData/bankCodes';

// 은행 select 표시용 — 원본(코드순, 백엔드와 정렬 일치)을 한글 가나다순으로 정렬한다(표시 전용).
// 코드 매칭/유효성은 원본 BANK_CODES 를 그대로 쓴다.
const BANK_CODES_SORTED = [...BANK_CODES].sort((a, b) => a.name.localeCompare(b.name, 'ko'));

/**
 * 가상계좌(VBANK) 관리자 승인 PG 환불 모달.
 * 고객 신청 환불계좌(returnInfo)를 prefill 하고, 은행/계좌번호/예금주 4필드를 수정 가능하게 노출한다.
 * [환불 실행] → window.confirm → POST admin/orders/:seq/vbankRefund(vbankRefundAction).
 * PG 성공 시 모달 닫고 목록 갱신(onRefunded), PG 실패("환불 실패: ...") 시 모달 유지하여 예금주/계좌 보정 후 재시도.
 *
 * @param {object} props
 * @param {boolean} props.openVbankRefundModal - 열기 트리거(열린 뒤 부모가 false 로 리셋)
 * @param {Function} props.setOpenVbankRefundModal - 트리거 리셋용 setter
 * @param {object|null} props.targetOrder - 대상 주문(returnInfo + detail 포함)
 * @param {Function} props.onRefunded - 환불 성공 시 호출(목록 갱신은 부모가 수행)
 */
const VbankRefundModal = ({openVbankRefundModal, setOpenVbankRefundModal, targetOrder, onRefunded}) => {
  const dispatch = useDispatch();

  const {vbankRefundLoading, vbankRefundDone, vbankRefundError, vbankRefund} = useSelector(state => state.order);

  /** @type {[{bankCode: string, bankName: string, accountNum: string, accountHolder: string}, Function]} */
  const [form, setForm] = useState({bankCode: '', bankName: '', accountNum: '', accountHolder: ''});

  // 열기 트리거 → 고객 신청 returnInfo 로 prefill 후 모달 표시. accountNum 은 returnInfo.bankNum 에서 매핑.
  useEffect(() => {
    if (openVbankRefundModal && targetOrder) {
      const ri = targetOrder.returnInfo || {};
      setForm({
        bankCode: ri.bankCode || '',
        bankName: ri.bankName || '',
        accountNum: ri.bankNum || '',
        accountHolder: ri.accountHolder || '',
      });
      UIKIT.modal('#vbankRefundModal').show();
      setOpenVbankRefundModal(false);
    }
  }, [openVbankRefundModal, targetOrder]);

  const onChangeBank = useCallback(e => {
    const bankCode = e.target.value;
    const selected = BANK_CODES.find(b => b.code === bankCode);
    setForm(prev => ({...prev, bankCode, bankName: selected ? selected.name : ''}));
  }, []);

  const onChangeField = useCallback((key, value) => {
    setForm(prev => ({...prev, [key]: value}));
  }, []);

  const onClickRefund = useCallback(() => {
    if (!targetOrder) {
      return;
    }

    // 환불 계좌 4필드 검증
    if (!form.bankCode || !form.bankName || !form.accountNum || !form.accountHolder) {
      alert('환불 계좌 정보를 모두 입력해 주세요. (은행/계좌번호/예금주명)');
      return;
    }

    // 취소요청(status=2) detail 만 환불 대상
    const detailCheckbox = {};
    if (targetOrder.detail) {
      for (let detail of targetOrder.detail) {
        if (detail.status === 2) {
          detailCheckbox[detail.seq] = {value: detail.seq, checked: true};
        }
      }
    }
    if (Object.keys(detailCheckbox).length <= 0) {
      alert('환불할 취소요청 상품이 없습니다.');
      return;
    }

    const reasonDetail = targetOrder.detail
      ? targetOrder.detail.find(d => d.status === 2 && d.cancelReason)
      : null;
    const message = reasonDetail ? reasonDetail.cancelReason : '관리자 요청';

    if (
      !window.confirm(
        `주문(${targetOrder.seq})을 가상계좌 환불 처리하시겠습니까?\n${form.bankName} ${form.accountNum} ${form.accountHolder}`,
      )
    ) {
      return;
    }

    dispatch(
      vbankRefundAction(targetOrder.seq, {
        detailCheckbox,
        message,
        refundAccount: {
          bankCode: form.bankCode,
          bankName: form.bankName,
          accountNum: form.accountNum,
          accountHolder: form.accountHolder,
        },
      }),
    );
  }, [dispatch, targetOrder, form]);

  // 환불 결과: PG 성공/실패 모두 HTTP 200 → done. msg 의 "환불 실패: " prefix 로 분기.
  useEffect(() => {
    if (vbankRefundDone) {
      const msg = vbankRefund && vbankRefund.msg ? vbankRefund.msg : '처리되었습니다.';
      alert(msg);
      const isFail = msg.indexOf('환불 실패') === 0;
      if (!isFail) {
        // 성공: 모달 닫고 목록 갱신
        UIKIT.modal('#vbankRefundModal').hide();
        if (onRefunded) {
          onRefunded();
        }
      }
      // 실패(PG 거절)면 모달 유지 → 예금주/계좌 보정 후 재시도
      dispatch(vbankRefundResetAction());
    }

    if (vbankRefundError) {
      alert(vbankRefundError.msg ? vbankRefundError.msg : '환불 요청 중 오류가 발생했습니다.');
      dispatch(vbankRefundResetAction());
    }
  }, [vbankRefundDone, vbankRefundError]);

  const ri = (targetOrder && targetOrder.returnInfo) || {};
  const allDetails = (targetOrder && targetOrder.detail) || [];
  const targetDetails = allDetails.filter(d => d.status === 2);

  // 환불 예정 금액 — 백엔드 ManagerVbankRefund 와 동일한 단일 소스(order.amountDeposited)를 사용한다.
  // amountDeposited = SetAmountDepositedPrice() 결과: status=2 상품가 합 ± 배송비
  // (전체취소 시 배송비 가산 / 부분취소가 무료배송 기준 미달 시 배송비 차감). 모든 주문 JSON 에 실려 있다.
  const productAmount = targetDetails.reduce((sum, d) => sum + d.price * d.qty, 0);
  const refundAmount =
    targetOrder && typeof targetOrder.amountDeposited === 'number' ? targetOrder.amountDeposited : productAmount;
  // 배송비(부호 포함) = 환불액 - 상품금액. 전체취소면 +배송비(환불), 부분취소 무료배송 미달이면 -배송비(차감).
  const deliveryAmount = refundAmount - productAmount;

  return (
    <div id="vbankRefundModal" className="uk-position-z-index-9999999" data-uk-modal="container:false;">
      <div className="uk-modal-dialog">
        <button className="uk-modal-close-default" type="button" data-uk-close></button>
        <div className="uk-modal-header uk-padding-small-horizontal uk-padding-horizontal@m">
          가상계좌 환불 {targetOrder && `(주문번호: ${targetOrder.seq})`}
        </div>
        <div className="uk-modal-body uk-padding-remove-horizontal uk-padding-horizontal@m uk-text-0_9">
          {/* 고객 신청 계좌(읽기전용 참조) */}
          <div className="uk-background-muted uk-padding-small uk-margin-small-bottom">
            <h5 className="uk-text-bold uk-margin-small-bottom">고객 신청 계좌 (참조)</h5>
            <ul className="uk-list uk-margin-remove">
              <li>은행 : {ri.bankName || '-'}</li>
              <li>계좌번호 : {ri.bankNum || '-'}</li>
              <li>예금주 : {ri.accountHolder || '-'}</li>
            </ul>
          </div>

          {/* 환불 계좌(수정 가능) */}
          <h5 className="uk-text-bold uk-margin-small-bottom">환불 계좌 (수정 가능)</h5>
          <div className="uk-margin-small">
            <label className="uk-form-label uk-display-block uk-margin-3-bottom">은행</label>
            <select className="uk-select uk-form-small" value={form.bankCode} onChange={onChangeBank}>
              <option value="">은행 선택</option>
              {BANK_CODES_SORTED.map(bank => (
                <option key={bank.code} value={bank.code}>
                  {bank.name}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-margin-small">
            <label className="uk-form-label uk-display-block uk-margin-3-bottom">계좌번호</label>
            <input
              type="text"
              className="uk-input uk-form-small"
              value={form.accountNum}
              placeholder="계좌번호('-' 없이)"
              onChange={e => onChangeField('accountNum', e.target.value)}
            />
          </div>
          <div className="uk-margin-small">
            <label className="uk-form-label uk-display-block uk-margin-3-bottom">
              예금주명
              <i
                className="mdi mdi-help-circle-outline uk-text-muted"
                style={{cursor: 'help', fontSize: '12px', verticalAlign: 'middle', marginLeft: '2px'}}
                data-uk-tooltip="title: 청소년/간편결제 계좌는 예금주명에 명칭을 포함하세요. 예: 홍길동(미니); pos: top"></i>
            </label>
            <input
              type="text"
              className="uk-input uk-form-small"
              value={form.accountHolder}
              placeholder="예금주명"
              onChange={e => onChangeField('accountHolder', e.target.value)}
            />
          </div>

          {/* 환불 대상 상품(읽기전용) */}
          <h5 className="uk-text-bold uk-margin-small-bottom uk-margin-small-top">
            환불 대상 상품 ({targetDetails.length}건)
          </h5>
          <ul className="uk-list uk-list-divider uk-margin-remove">
            {targetDetails.map(detail => (
              <li key={detail.seq}>
                {detail.product && detail.product.name ? detail.product.name : '삭제된 상품'}
                {detail.option_name ? ` / ${detail.option_name}` : ''} / {detail.qty}개 /{' '}
                {(detail.price * detail.qty).toLocaleString()}원
              </li>
            ))}
          </ul>

          {/* 환불 예정 금액(상품/배송비/합계) — 상품가만 보이면 배송비 포함 여부가 혼동되므로 실제 환불액을 명시. */}
          <div className="uk-background-muted uk-padding-small uk-margin-small-top">
            <div className="uk-flex uk-flex-between">
              <span>상품 금액</span>
              <span>{productAmount.toLocaleString()}원</span>
            </div>
            {deliveryAmount !== 0 && (
              <div className="uk-flex uk-flex-between uk-margin-3-top">
                <span>{deliveryAmount < 0 ? '배송비 차감' : '배송비'}</span>
                <span>{Math.abs(deliveryAmount).toLocaleString()}원</span>
              </div>
            )}
            <div className="uk-flex uk-flex-between uk-text-bold uk-margin-small-top">
              <span>환불 예정 금액</span>
              <span>{refundAmount.toLocaleString()}원</span>
            </div>
          </div>

          <div className="uk-margin-medium-top uk-text-right">
            <button className="uk-button uk-button-default uk-modal-close uk-margin-small-right" type="button">
              닫기
            </button>
            {vbankRefundLoading ? (
              <button className="uk-button uk-button-danger" type="button" disabled>
                <div data-uk-spinner></div>
              </button>
            ) : (
              <button className="uk-button uk-button-danger" type="button" onClick={onClickRefund}>
                환불
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VbankRefundModal;
